import { random } from 'lodash'

/**
 * Like lodash `sample`, but earlier elements are more likely to be selected. Probability decays linearly: the first
 * element is more likely than the second, which is more likely than the third, etc.
 */
export function decayingSample<T>(array: readonly T[]): T | undefined {
  if (array.length === 0) return undefined

  const n = array.length
  // Sum of weights: n + (n-1) + ... + 1 = n*(n+1)/2
  const totalWeight = (n * (n + 1)) / 2
  let roll = random(0, totalWeight - 1)

  // I am bad at math, but here's the Claude Opus 4.6 explanation:
  //
  // > Each element gets a weight equal to `n - i` (so for 5 elements: `[5, 4, 3, 2, 1]`). The total weight is
  // > `n*(n+1)/2 = 15`.
  // >
  // > `roll` is a random integer in `[0, 14]`. The loop walks through the array, subtracting each element's weight from
  // > `roll`. When `roll` goes negative, that element is selected.
  // >
  // > Visually, you're picking a random point on a number line divided into segments:
  // >
  // > |  a (5)  | b (4) | c (3) | d (2) |e(1)|
  // > 0         5       9       12      14   15
  // >
  // > If `roll = 7`:
  // > - Subtract 5 (weight of `a`) → `roll = 2`, still >= 0, continue
  // > - Subtract 4 (weight of `b`) → `roll = -2`, negative → return b
  // >
  // > This gives each element probability proportional to its weight — a has `a` 5/15 chance, `e` has 1/15.
  for (let i = 0; i < n; i++) {
    roll -= n - i
    if (roll < 0) return array[i]
  }

  return array[n - 1]
}
